'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { CheckBadge, ShieldChecked, Thunder } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';
import Image from 'next/image';

export default function BankHero() {
  const t = useTranslations('Banks.Hero');


  const keyFeatures = [
    {
      titleKey: 'feature_tips_title',
      descKey: 'feature_tips_desc',
      icon: <CheckBadge width={25} height={25} className="shrink-0" />
    },
    {
      titleKey: 'feature_fast_title',
      descKey: 'feature_fast_desc',
      icon: <Thunder width={25} height={25} className="shrink-0" />
    },
    {
      titleKey: 'feature_white_label_title',
      descKey: 'feature_white_label_desc',
      icon: <ShieldChecked width={25} height={25} className="shrink-0" />
    },
  ];

  return (
    <motion.section
      aria-labelledby="bank-hero-heading"
      className="w-full bg-white py-12 sm:py-20 px-6 sm:px-10 lg:px-16"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-5 space-y-8">
          <div className="space-y-4">
            <span className="hero-fade-up text-xs sm:text-sm font-semibold tracking-widest color-primary uppercase">
              {t('badge_text')}
            </span>
            <h1 id="bank-hero-heading" className="text-3xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 leading-[1.15] tracking-tight">
              {t.rich('main_title', {
                highlight: (chunks) => <span className="color-primary">{chunks}</span>,
              })}
            </h1>
            <p className="text-sm sm:text-base font-normal leading-relaxed pt-2">
              {t('sub_description')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Link
              href="#how-it-works"
              className="btn-blink-hover inline-flex items-center justify-center gap-2 bg-primary text-white text-sm font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
            >
              {t('btn_how_it_works')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4">
            {keyFeatures.map((item) => (
              <div key={item.titleKey} className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {item.icon}
                </div>
                <div className="space-y-0.5">
                  <h3 className="text-xs sm:text-sm font-semibold color-primary leading-snug">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-gray-500 leading-tight">
                    {t(item.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>

        <div className="lg:col-span-7 relative flex justify-center items-center">
          <div className="relative w-full aspect-[4/3] rounded-3xl">
            <Image
              src="/images/banks/hero.webp"
              alt="Merchant accepting Kuik QR payment"
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-contain"
            />
          </div>
        </div>
      </div>
    </motion.section>
  );
}